import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API } from './api';
import { applyAccent } from './accentTheme';

// Resolve which workshop a sign-in screen belongs to. A login page is
// unauthenticated, so the slug comes either from the URL (`?w=slug`, for a link
// a workshop hands to its staff) or from the last workshop that signed in on
// this device. Returns an empty name and logo for the founding account and for
// anyone we cannot identify — the caller then shows the Driveline mark alone.
export const useWorkshopBranding = () => {
  const location = useLocation();
  const [branding, setBranding] = useState({ name: '', logo: '', isFounding: false, accent: '' });

  useEffect(() => {
    let revoked = false;
    let objectUrl = '';
    const slug = new URLSearchParams(location.search).get('w')
      || (() => { try { return window.localStorage.getItem('driveline:workshop'); } catch { return null; } })();
    if (!slug) return undefined;

    (async () => {
      try {
        const res = await fetch(`${API}/public/workshop/${encodeURIComponent(slug)}/branding`);
        if (!res.ok) return;
        const data = await res.json();
        if (revoked) return;
        const base = { name: data.workshop_name || '', isFounding: Boolean(data.is_founding),
                       accent: data.brand_color || '' };
        // The sign-in screen is the first thing a workshop's staff see, so it
        // carries their colour too — not just their logo.
        if (!data.is_founding) applyAccent(data.brand_color || '');
        if (!data.has_logo) { setBranding({ ...base, logo: '' }); return; }
        const img = await fetch(`${API}/public/workshop/${encodeURIComponent(slug)}/logo`);
        if (!img.ok || revoked) return;
        objectUrl = URL.createObjectURL(await img.blob());
        setBranding({ ...base, logo: objectUrl });
      } catch {
        /* a sign-in screen must never break because branding failed to load */
      }
    })();

    return () => { revoked = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [location.search]);

  return branding;
};
